// Shared hook for the `/api/fastapi/stats` probe. The same response powers
// both the empty-state stats badge and the chat-input "LLM not configured"
// gating, so we fetch once and share. Re-fetches on window focus so users
// returning from /settings after fixing config see the input re-enable
// without a full reload.
import { useCallback, useEffect, useState } from 'react';

export interface Stats {
  docs: number;
  actors: number;
  families: number;
  llm_status?: string;
}

export interface LlmStatusResult {
  stats: Stats | null;
  llmStatus: string | undefined;
  llmOk: boolean;
  refetch: () => void;
}

export function useLlmStatus(): LlmStatusResult {
  const [stats, setStats] = useState<Stats | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/fastapi/stats', {
      credentials: 'same-origin',
      signal: AbortSignal.timeout(5000),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: Stats | null) => {
        if (!cancelled && data) setStats(data);
      })
      .catch(() => {
        /* stats are best-effort */
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  useEffect(() => {
    const onFocus = () => refetch();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refetch]);

  const llmStatus = stats?.llm_status;
  const llmOk = llmStatus === 'ok';
  return { stats, llmStatus, llmOk, refetch };
}
